import { useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import { useSocket } from '@/contexts/SocketContext';
import { channelDashboardApi } from '../services/channelDashboardApi';

interface ListParams {
  mediaId?: string;
  status?: string;
  page?: number;
  limit?: number;
}

const commentsKey = (channelId: string | undefined, params?: ListParams): unknown[] =>
  ['channel-dashboard', channelId, 'comments', params ?? {}];

const invalidate = (queryClient: ReturnType<typeof useQueryClient>, channelId: string | undefined): void => {
  void queryClient.invalidateQueries({ queryKey: ['channel-dashboard', channelId, 'comments'], exact: false });
};

export const useChannelCommentsList = (channelId: string | undefined, params?: ListParams, enabled = true) =>
  useQuery({
    queryKey: commentsKey(channelId, params),
    queryFn: () => channelDashboardApi.listComments(channelId!, params),
    enabled: !!channelId && enabled,
    staleTime: 30_000,
  });

export const useReplyComment = (channelId: string | undefined) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ commentId, message }: { commentId: string; message: string }) =>
      channelDashboardApi.replyComment(channelId!, commentId, message),
    onSuccess: () => {
      toast.success('Resposta enviada');
      invalidate(queryClient, channelId);
    },
    onError: (error) => {
      const ax = error as { response?: { data?: { message?: string } } };
      toast.error(ax.response?.data?.message ?? 'Falha ao responder');
    },
  });
};

export const useHideComment = (channelId: string | undefined) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ commentId, hidden }: { commentId: string; hidden: boolean }) =>
      channelDashboardApi.hideComment(channelId!, commentId, hidden),
    onSuccess: (_data, vars) => {
      toast.success(vars.hidden ? 'Comentário ocultado' : 'Comentário reexibido');
      invalidate(queryClient, channelId);
    },
    onError: (error) => {
      const ax = error as { response?: { data?: { message?: string } } };
      toast.error(ax.response?.data?.message ?? 'Falha na moderação');
    },
  });
};

export const useDeleteComment = (channelId: string | undefined) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (commentId: string) => channelDashboardApi.deleteComment(channelId!, commentId),
    onSuccess: () => {
      toast.success('Comentário excluído');
      invalidate(queryClient, channelId);
    },
    onError: (error) => {
      const ax = error as { response?: { data?: { message?: string } } };
      toast.error(ax.response?.data?.message ?? 'Falha ao excluir');
    },
  });
};

/** Escuta channel:comment-received e invalida a lista (string literal — convenção channels). */
export const useChannelCommentsSocket = (channelId: string | undefined): void => {
  const { socket } = useSocket();
  const queryClient = useQueryClient();
  useEffect(() => {
    if (!socket || !channelId) return;
    const onReceived = (e: { channelId: string }): void => {
      if (e.channelId === channelId) invalidate(queryClient, channelId);
    };
    socket.on('channel:comment-received', onReceived);
    return () => {
      socket.off('channel:comment-received', onReceived);
    };
  }, [socket, channelId, queryClient]);
};
